/**
 * A Mystic's front door (mystic-page 2026-10-29, OUTLINE decision 2; API.md
 * §2 to §4). The canonical address is `{sub}.mysticaida.ai`; `/m/{sub}` is
 * the same page anywhere. The device keeps one id for the first-reading
 * guard, plus a light hint.
 */
const MYSTIC_DOMAIN = 'mysticaida.ai'
// The app's, the site's and the services' own hosts: never a Mystic.
const RESERVED = new Set(['iam', 'www', 'app', 'dev', 'api', 'share', 'syndicate', 'kindle', 'staging', 'admin', 'mail', 'static', 'cdn', 'm'])

/** The Mystic a host is the front door of, or ''. */
export function mysticSubFromHost (hostname) {
  const h = String(hostname || '').toLowerCase()
  if (!h.endsWith(`.${MYSTIC_DOMAIN}`)) return ''
  const sub = h.slice(0, -(MYSTIC_DOMAIN.length + 1))
  if (!sub || sub.includes('.') || RESERVED.has(sub) || !/^[a-z0-9][a-z0-9-]*$/.test(sub)) return ''
  return sub
}

/**
 * The front door's Mystic for a location, or '' for the app: `/m/{sub}`, or
 * the bare `{sub}.mysticaida.ai` (a `?via=` still counts as bare). Anything
 * the app reads from the URL (a reader, a panel, a reading) is the app.
 */
export function frontDoorSub ({ hostname = '', pathname = '/', search = '' } = {}) {
  const m = String(pathname || '').match(/^\/m\/([^/?#]+)\/?$/)
  if (m) return decodeURIComponent(m[1]).toLowerCase()
  const sub = mysticSubFromHost(hostname)
  if (!sub || (pathname && pathname !== '/')) return ''
  const keys = [...new URLSearchParams(search || '').keys()].filter((k) => !/^(via|utm_[a-z]+|fbclid|gclid)$/.test(k))
  return keys.length ? '' : sub
}

export const DEVICE_COOKIE = 'ma_device'
const YEAR = 365 * 24 * 3600

function readCookie (name) {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return m ? decodeURIComponent(m[1]) : ''
  } catch (e) { return '' }
}
function newId () {
  try { if (crypto && crypto.randomUUID) return crypto.randomUUID() } catch (e) { /* */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`
}

/** The device's one id: the cookie, else the backup, else a new one; both kept. */
export function deviceId () {
  let id = readCookie(DEVICE_COOKIE)
  if (!id) { try { id = localStorage.getItem(DEVICE_COOKIE) || '' } catch (e) { id = '' } }
  if (!id) id = newId()
  try { document.cookie = `${DEVICE_COOKIE}=${encodeURIComponent(id)}; max-age=${YEAR}; path=/; samesite=lax` } catch (e) { /* */ }
  try { localStorage.setItem(DEVICE_COOKIE, id) } catch (e) { /* */ }
  return id
}

/** A light device check: a hash of user agent, screen, timezone and language. Nothing readable. */
export function deviceHint () {
  let parts = ''
  try {
    const s = window.screen || {}
    let tz = ''
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch (e) { /* */ }
    parts = [navigator.userAgent, `${s.width}x${s.height}x${s.colorDepth}`, tz, navigator.language].join('|')
  } catch (e) { parts = '' }
  // FNV-1a, twice with different seeds: a short, stable, one-way hint.
  const fnv = (str, seed) => {
    let h = seed >>> 0
    for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0 }
    return h.toString(16).padStart(8, '0')
  }
  return fnv(parts, 2166136261) + fnv(parts, 0x9747b28c)
}

/** The guest-place / first-reading fields (API.md §4). */
export function deviceParams () {
  return { device_id: deviceId(), device_hint: deviceHint() }
}
